// src/components/Admin/AdminProgressTracker.jsx
import React, { useEffect, useState } from "react";
import { addDoc, collection, getDocs } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db } from "../../firebaseConfig";
import { toStorageDate } from "../../utils/date";

const TIPURI_TRASEU = ["MAI", "MAPN", "ISU", "ANP"];

const AdminProgressTracker = () => {
  const navigate = useNavigate();
  const [participants, setParticipants] = useState([]);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    userId: "",
    userName: "",
    userEmail: "",
    tipTraseu: "",
    data: "",
    timpTraseu: "",
    timpRezistenta: "",
  });

  // helpers
  const handleFieldChange = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSelectParticipant = (value) => {
    const p = participants.find(
      (x) => x.selector === value || x.email === value || x.id === value
    );
    setForm((prev) => ({
      ...prev,
      userId: p?.selector || value,
      userName: p?.name || "",
      userEmail: p?.email || "",
      tipTraseu: p?.tipTraseu || prev.tipTraseu || "MAI",
    }));
  };


  const timeToSeconds = (str) => {
    if (!str) return null;
    const parts = str.split(":");
    if (parts.length !== 2) return null;
    const [m, s] = parts.map(Number);
    if (Number.isNaN(m) || Number.isNaN(s) || m < 0 || s < 0 || s > 59) return null;
    return m * 60 + s;
  };

  // data loading
  useEffect(() => {
    const fetchParticipants = async () => {
      const fromUsers = await getDocs(collection(db, "users"));
      const map = new Map();

      const addParticipant = (obj) => {
        const selector = (obj.uid || obj.userId || obj.email || obj.id || "").toLowerCase();
        if (!selector) return;
        if (map.has(selector)) return;
        map.set(selector, {
          id: obj.id || selector,
          selector,
          email: obj.email || obj.userEmail || "",
          name: obj.numePrenume || obj.nume || obj.userName || obj.name || "Participant",
          tipTraseu: (obj.tipTraseu || "MAI").toUpperCase(),
        });
      };

      fromUsers.forEach((doc) =>
        addParticipant({
          id: doc.id,
          uid: doc.data().uid || doc.id,
          email: doc.data().email,
          userName: doc.data().displayName || doc.data().name,
          name: doc.data().displayName || doc.data().name,
        })
      );

      const arr = Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
      setParticipants(arr);
    };
    fetchParticipants();
  }, []);

  // save progress
  const handleSave = async () => {
    if (!form.userId) return alert("Selecteaza un user.");
    if (!form.tipTraseu) return alert("Selecteaza tipul de traseu.");
    if (!form.timpTraseu && !form.timpRezistenta)
      return alert("Completeaza cel putin un camp de progres.");

    const timpTraseuSec = timeToSeconds(form.timpTraseu);
    const timpRezSec = timeToSeconds(form.timpRezistenta);
    const dataFormatted = toStorageDate(form.data || new Date());
    if (!dataFormatted) {
      return alert("Data trebuie sa fie in format dd/mm/yyyy.");
    }

    if (form.timpTraseu && timpTraseuSec === null)
      return alert("Timp traseu trebuie sa fie in format mm:ss (ex: 03:20).");
    if (form.timpRezistenta && timpRezSec === null)
      return alert("Timp rezistenta trebuie sa fie in format mm:ss (ex: 01:45).");

    const payload = {
      userId: form.userId,
      userName: form.userName,
      userEmail: form.userEmail,
      tipTraseu: form.tipTraseu,
      data: dataFormatted,
      timpTraseu: timpTraseuSec,
      timpRezistenta: timpRezSec,
      createdAt: new Date().toISOString(),
    };

    try {
      setSaving(true);
      await addDoc(collection(db, "user_progress"), payload);
      setForm((prev) => ({
        ...prev,
        data: payload.data,
        timpTraseu: "",
        timpRezistenta: "",
      }));
      alert("Progres salvat.");
    } catch (err) {
      console.error("Eroare salvare progres:", err);
      alert("Nu am putut salva progresul.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-card admin-progress-card admin-progress admin-row2-card">
      <h3 className="admin-card-header">Urmărire progres</h3>

      <div className="admin-progress-form">
        <label>
          User
          <select
            value={form.userId}
            onChange={(e) => handleSelectParticipant(e.target.value)}
          >
            <option value="">Selecteaza user</option>
            {participants.map((p) => {
              const key = p.selector || p.id;
              const display = p.email
                ? `${p.name} (${p.email})`
                : `${p.name}`;
              return (
                <option key={key} value={key}>
                  {display}
                </option>
              );
            })}
          </select>
        </label>

        <div className="admin-progress-grid">
          <label>
            Tip traseu
            <select
              value={form.tipTraseu}
              onChange={(e) => handleFieldChange("tipTraseu", e.target.value)}
            >
              <option value="">Selecteaza tipul</option>
              {TIPURI_TRASEU.map((tip) => (
                <option key={tip} value={tip}>
                  {tip}
                </option>
              ))}
            </select>
          </label>

          <label>
            Data
            <input
              type="text"
              placeholder="zz/ll/yyyy"
              value={form.data}
              onChange={(e) => handleFieldChange("data", e.target.value)}
            />
          </label>
        </div>

        <div className="admin-progress-grid">
          <label>
            Timp traseu (mm:ss)
            <input
              type="text"
              value={form.timpTraseu}
              onChange={(e) => handleFieldChange("timpTraseu", e.target.value)}
              placeholder="ex: 03:20"
            />
          </label>

          <label>
            Timp rezistenta (mm:ss)
            <input
              type="text"
              value={form.timpRezistenta}
              onChange={(e) =>
                handleFieldChange("timpRezistenta", e.target.value)
              }
              placeholder="ex: 01:45"
            />
          </label>
        </div>

        <div className="admin-progress-actions">
          <button onClick={handleSave} disabled={saving}>
            {saving ? "Se salveaza..." : "Salveaza progresul"}
          </button>

          <button
            type="button"
            className="secondary"
            onClick={() => {
              if (!form.userId || !form.tipTraseu) {
                alert("Selecteaza userul si tipul de traseu.");
                return;
              }
              const identifier =
                form.userId ||
                participants.find(
                  (p) => p.selector === form.userId || p.id === form.userId
                )?.selector;
              const slug = form.tipTraseu.toLowerCase();
              navigate(
                `/progres/${slug}?userId=${encodeURIComponent(
                  identifier
                )}&userName=${encodeURIComponent(form.userName || "")}`
              );
            }}
          >
            Urmareste progresul
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminProgressTracker;
